import { useRef, useEffect, useState, memo } from "react";
import Webcam from "react-webcam";
import { useParams } from "react-router-dom";
import {
  Button,
  ButtonVariants,
  Svg,
  Title,
  TitleSize,
} from "@axa-fr/design-system-look-and-feel-react";
import { DOCUMENT_FORMATS } from "./utils/documentFormats";
import { useSessionInfo } from "./hooks/useSessionInfo";
import { useTouchDevice } from "./hooks/useTouchDevice";
import { useZoomLoupe } from "./hooks/useZoomLoupe";
import FormatSelector from "./components/FormatSelector";
import DocumentMask from "./components/DocumentMask";
import ScreenshotPreview from "./components/ScreenshotPreview";
import Loader from "./components/Loader";
import styles from "./Capture.module.css";
import { useScreenshot } from "./hooks/useScreenshot";
import { useUploadDocument } from "./hooks/useUploadDocument";
import camera from "@material-symbols/svg-400/outlined/camera.svg";

declare global {
  interface Window {
    cv: any;
  }
}

export interface DocumentDetectionResult {
  corners: { x: number; y: number }[];
  confidence: number;
  boundingRect: { x: number; y: number; width: number; height: number };
  isDocument: boolean;
}

const MemoizedScreenshotPreview = memo(ScreenshotPreview);
const MemoizedDocumentMask = memo(DocumentMask);

const Capture = () => {
  const { sessionId } = useParams<{ sessionId: string }>();
  const { sessionInfo, isLoading, error, fetchSessionInfo } =
    useSessionInfo(sessionId);
  const webcamRef = useRef<Webcam>(null);
  const canvasRef = useRef<HTMLCanvasElement>(
    null
  ) as React.RefObject<HTMLCanvasElement>;

  const { screenshot, screenshotDims, setScreenshot, capturePhoto, imgRef } =
    useScreenshot(webcamRef, canvasRef, isLoading);

  const [canvasDims, setCanvasDims] = useState({ width: 0, height: 0 });

  const { zoomData, setZoomData } = useZoomLoupe();

  const { handleUpload, uploadError } = useUploadDocument(
    sessionId,
    fetchSessionInfo,
    setScreenshot
  );

  const touchDevice = useTouchDevice();

  const [selectedFormatValue, setSelectedFormatValue] = useState<string | null>(
    null
  );

  const [isVideoReady, setIsVideoReady] = useState(false);
  const [cameraDenied, setCameraDenied] = useState(false);

  const captureCountRef = useRef(1);

  useEffect(() => {
    if (
      !sessionInfo ||
      sessionInfo.documentUrls.length >= sessionInfo.documentCount ||
      screenshot
    ) {
      return;
    }

    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .then((stream) => {
        setCameraDenied(false);
        if (webcamRef.current) {
          setIsVideoReady(true);
          webcamRef.current.video!.srcObject = stream;
        }
      })
      .catch((err) => {
        if (
          err &&
          (err.name === "NotAllowedError" ||
            err.name === "PermissionDeniedError")
        ) {
          setCameraDenied(true);
        }
      });

    let animationFrameId: number;
    const render = async () => {
      if (!window.cv) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }
      if (!webcamRef.current || !canvasRef.current) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }
      if (screenshot || isLoading) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      const video = webcamRef.current.video as HTMLVideoElement;
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");

      if (
        !video ||
        !video.videoWidth ||
        !video.videoHeight ||
        video.videoWidth <= 0 ||
        video.videoHeight <= 0
      ) {
        animationFrameId = requestAnimationFrame(render);
        setIsVideoReady(false);
        return;
      }

      setIsVideoReady(true);

      if (
        canvasDims.width !== video.videoWidth ||
        canvasDims.height !== video.videoHeight
      ) {
        setCanvasDims({ width: video.videoWidth, height: video.videoHeight });
      }
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      if (ctx) {
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      }
      animationFrameId = requestAnimationFrame(render);
    };
    animationFrameId = requestAnimationFrame(render);
    return () => {
      cancelAnimationFrame(animationFrameId);
    };
  }, [screenshot, isLoading, sessionInfo, canvasDims]);

  useEffect(() => {
    const tryPlayVideo = () => {
      const video = webcamRef.current?.video as HTMLVideoElement | undefined;
      if (video && video.paused) {
        video.play().catch((e) => {
          console.warn("Impossible de démarrer la vidéo automatiquement :", e);
        });
      }
    };
    const interval = setInterval(tryPlayVideo, 200);
    return () => clearInterval(interval);
  }, []);

  const formats = sessionInfo?.documentType
    ? DOCUMENT_FORMATS[sessionInfo.documentType]
    : undefined;

  useEffect(() => {
    if (formats && formats.length > 0) {
      setSelectedFormatValue(formats[0].value);
    }
  }, [sessionInfo?.documentType]);

  useEffect(() => {
    const video = webcamRef.current?.video as HTMLVideoElement | undefined;
    if (!video) return;
    const handleLoadedData = () => setIsVideoReady(true);
    video.addEventListener("loadeddata", handleLoadedData);
    return () => {
      video.removeEventListener("loadeddata", handleLoadedData);
    };
  }, [webcamRef.current]);

  useEffect(() => {
    function updateCanvasDims() {
      if (canvasRef.current) {
        const rect = canvasRef.current.getBoundingClientRect();
        setCanvasDims({ width: rect.width, height: rect.height });
      }
    }

    if (!screenshot) {
      updateCanvasDims();
      window.addEventListener("resize", updateCanvasDims);
      return () => window.removeEventListener("resize", updateCanvasDims);
    }
  }, [screenshot]);

  if (cameraDenied) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>
          Accès à la caméra refusé.
          <br />
          Veuillez autoriser l'accès à la caméra dans votre navigateur puis
          rechargez la page.
          <br />
          <Button
            variant={ButtonVariants.primary}
            onClick={() => window.location.reload()}
          >
            Recharger la page
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return <div className={styles.container}>Erreur : {error}</div>;
  }

  if (!sessionInfo || (isLoading && !screenshot)) {
    return <Loader />;
  }

  if (sessionInfo.documentUrls.length >= sessionInfo.documentCount) {
    return (
      <div className={`${styles.container} ${styles.successContainer}`}>
        <div className={styles.successIcon}>✓</div>
        <h1 className={styles.successMessage}>
          Tous vos documents ont été téléchargés.
        </h1>
      </div>
    );
  }

  if (screenshot) {
    return (
      <MemoizedScreenshotPreview
        screenshot={screenshot}
        screenshotDims={screenshotDims}
        zoomData={zoomData}
        uploadError={uploadError}
        setZoomData={setZoomData}
        touchDevice={touchDevice}
        isLoading={isLoading}
        onRetake={() => setScreenshot(null)}
        onValidate={() => {
          handleUpload(screenshot, `capture-${captureCountRef.current}.jpg`);
          captureCountRef.current += 1;
        }}
        imgRef={imgRef}
      />
    );
  }

  return (
    <div className={styles.container}>
      <Title level={1} size={TitleSize.L}>
        Capture du document{" "}
        {sessionInfo.documentType && `(${sessionInfo.documentType})`}{" "}
        {`${sessionInfo.documentUrls.length + 1} / ${
          sessionInfo.documentCount
        }`}
      </Title>
      {formats && formats.length > 1 && (
        <div
          style={{
            margin: "8px 0",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <FormatSelector
            formats={formats}
            selectedFormatValue={selectedFormatValue}
            onChange={setSelectedFormatValue}
          />
        </div>
      )}
      {isVideoReady && (
        <div
          style={{
            position: "relative",
          }}
        >
          <canvas
            ref={canvasRef}
            className={styles.canvas}
            will-read-frequently="true"
          />

          <MemoizedDocumentMask
            documentType={sessionInfo.documentType}
            canvasDims={canvasDims}
            selectedFormatValue={selectedFormatValue}
            isVideoReady={isVideoReady}
            canvasRef={canvasRef}
            className={styles.mask}
          />
        </div>
      )}

      {!screenshot &&
        sessionInfo &&
        sessionInfo.documentUrls.length < sessionInfo.documentCount && (
          <Webcam
            ref={webcamRef}
            audio={false}
            screenshotFormat="image/jpeg"
            style={{
              position: "absolute",
              left: "-9999px",
              width: "1px",
              height: "1px",
            }}
            videoConstraints={{
              facingMode: { exact: "environment" },
            }}
          />
        )}
      <Button
        iconLeft={<Svg src={camera} alt="Capture" />}
        onClick={capturePhoto}
        aria-label="Prendre une photo"
      >
        Capture
      </Button>
    </div>
  );
};

export default Capture;
